import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DateTimeFormatter, NameFormatter } from '../../shared/controls/formatters';
import {
  getCommonTextFilterParams,
  getSetCheckboxFilterParams,
  getSetDatefilterParams,
  getSetFilterParams,
} from '../../utils/agGridFilters';
import { isMobile } from '../../utils/deviceUtils';
import GridOptionButton from '../sharedChild/gridOptionButton';

const useContactColumnDefs = ({
  hasUpdateContactPermission,
  hasInfoContactPermission,
  hasDeleteContactPermission,
  campaignListDropdown,
  tagsList,
  leadSourceList,
  contactPersonRole,
  salutation,
  owner,
  gridData,
  open,
  handleClickDelete,
}) => {
  const navigate = useNavigate();
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const checkboxFilterParams = useMemo(() => getSetCheckboxFilterParams, []);
  const setFilterParams = useMemo(() => getSetFilterParams, []);
    const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);
  const handleOpenEditForm = useCallback(
    (paramsValue) => {
      navigate('/contacts/editContact', {
        state: paramsValue,
      });
    },
    [navigate]
  );

  const handleOpenViewForm = useCallback(
    (paramsValue) => {
      navigate(
        `/contacts/viewContact?id=${paramsValue.id}&pageNo=${gridData?.inputPayload?.pageNumber}`,
        {
          state: paramsValue,
        }
      );
    },
    [navigate, gridData]
  );
  const actionsColumn = useMemo(() => {
    if (!(hasUpdateContactPermission || hasInfoContactPermission || hasDeleteContactPermission))
      return [];

    return [
      {
        headerName: 'Actions',
        headerCheckboxSelection: (params) => params?.node && !params.node.group,
        checkboxSelection: (params) => params?.node && !params.node.group,
        cellRenderer: (params) => {
          if (!params?.node || params.node.group) return null;
          const rowData = { ...params.data, rowIndex: params.rowIndex };
          return (
            <GridOptionButton
              hasInfoContactPermission={hasInfoContactPermission}
              hasDeleteContactPermission={hasDeleteContactPermission}
              hasUpdateContactPermission={hasUpdateContactPermission}
              rowData={rowData}
              openPopup={open}
              handleOpenEditForm={() => handleOpenEditForm(rowData)}
              handleClickDelete={() => handleClickDelete(rowData)}
              handleOpenViewForm={() => handleOpenViewForm(rowData)}
            />
          );
        },
        maxWidth: 100,
        sortable: false,
        filter: false,
        minWidth: 100,
        pinned: 'left',
      },
    ];
  }, [
    hasUpdateContactPermission,
    hasInfoContactPermission,
    hasDeleteContactPermission,
    open,
    handleOpenEditForm,
    handleOpenViewForm,
    handleClickDelete,
  ]);

  const columnDefs = useMemo(() => {
    if (!leadSourceList?.length || !owner?.length) return [];

    return [
      ...actionsColumn,
      {
        headerName: 'Full Name',
        field: 'fullName',
        pinned: isMobile() ? null : 'left',
        minWidth: 180,
        cellRenderer: (params) => {
          const name = params.value;
          if (!name || params.node.group) return name;
          return hasInfoContactPermission ? (
            <Link
              to={`/contacts/viewContact?id=${params?.data?.id}&pageNo=${gridData?.inputPayload?.pageNumber}`}
              state={params.data}
              style={{ fontWeight: '600' }}
            >
              {name}
            </Link>
          ) : (
            <span style={{ fontWeight: '600' }}>{name}</span>
          );
        },
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Account Name',
        field: 'account',
        pinned: isMobile() ? null : 'left',
        minWidth: 180,
        type: 'dropdown',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Campaign Name',
        field: 'campaignId',
        valueGetter: (params) => params?.data?.campaignName,
        filter: 'agSetColumnFilter',
        filterParams: setFilterParams(campaignListDropdown?.map((c) => c.campaignName)),
        type: 'text',
        // hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Email',
        field: 'email',
        type: 'text',
        cellRenderer: (params) =>
          params.value ? <a href={`mailto:${params.value}`}>{params.value}</a> : null,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Phone',
        field: 'phone',
        type: 'number',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Contact Owner',
        field: 'owner',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: setFilterParams(owner?.map((o) => o.display_name)),
      },
      {
        headerName: 'Title',
        field: 'title',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Department',
        field: 'department',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'LinkedIn',
        field: 'linkedin',
        type: 'text',
        cellRenderer: (params) =>
          params.value ? (
            <a
              href={params.value.startsWith('http') ? params.value : `https://${params.value}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {params.value}
            </a>
          ) : null,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Lead Source',
        field: 'leadSource',
        type: 'text',
        filter: 'agSetColumnFilter',
        filterParams: setFilterParams(leadSourceList?.map((l) => l.displayName)),
      },
      {
        headerName: 'State',
        field: 'state',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Zip Code',
        field: 'zipCode',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Created By',
        field: 'createdBy',
        valueFormatter: NameFormatter,
        minWidth: 230,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Updated By',
        field: 'updatedBy',
        valueFormatter: NameFormatter,
        minWidth: 230,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Created Date Time(UTC)',
        field: 'createdDateTime',
        valueFormatter: DateTimeFormatter,
        minWidth: 230,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        // hide: true,
      },
      {
        headerName: 'Updated Date Time(UTC)',
        field: 'updatedDateTime',
        valueFormatter: DateTimeFormatter,
        minWidth: 230,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        // hide: true,
      },
      {
        headerName: 'Tags',
        field: 'tagIds',
        valueGetter: (params) => params?.data?.tags,
        filter: 'agSetColumnFilter',
        filterParams: setFilterParams(tagsList?.map((t) => t.name)),
        // hide: true,
      },
      {
        headerName: 'Secondary Email',
        field: 'secondaryEmail',
        type: 'text',
        cellRenderer: (params) =>
          params.value ? <a href={`mailto:${params.value}`}>{params.value}</a> : null,
        // hide: true,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Mobile',
        field: 'mobile',
        type: 'number',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Twitter',
        field: 'twitter',
        type: 'text',
        cellRenderer: (params) =>
          params.value ? (
            <a
              href={params.value.startsWith('http') ? params.value : `https://${params.value}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {params.value}
            </a>
          ) : null,
        // hide: true,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Facebook',
        field: 'facebook',
        type: 'text',
        filter: 'agTextColumnFilter',
        cellRenderer: (params) => {
          if (!params.value) return null; // Return null if the value is empty or undefined

          const url = params.value.startsWith('http') ? params.value : `https://${params.value}`;
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {params.value}
            </a>
          );
        },
        // hide: true,
        filterParams: commonTextFilterParams,
      },

      {
        headerName: 'Reporting To',
        field: 'reportingTo',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Assistant',
        field: 'assistant',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Assistant Phone',
        field: 'assistantPhone',
        type: 'number',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Email Opt Out',
        field: 'emailOptOut',
        type: 'checkbox',
        filter: 'agSetColumnFilter',
        filterParams: checkboxFilterParams,
        // hide:true
      },
      {
        headerName: 'Contact Person Role',
        field: 'contactPersonRole',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: setFilterParams(contactPersonRole?.map((t) => t.name)),
        // hide: true,
      },

      {
        headerName: 'Street',
        field: 'street',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'City',
        field: 'city',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Country',
        field: 'country',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Technology Stack',
        field: 'technologyStack',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Skype ID',
        field: 'skypeId',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Description',
        field: 'description',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'First Name',
        field: 'firstName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Middle Name',
        field: 'middleName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Last Name',
        field: 'lastName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Salutation',
        field: 'salutation',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: setFilterParams(salutation?.map((t) => t.name)),
        // hide: true,
      },
    ];
  }, [
    hasInfoContactPermission,
    leadSourceList,
    owner,
    campaignListDropdown,
    tagsList,
    gridData,
    actionsColumn,
    commonTextFilterParams,
    setFilterParams,
  ]);

  return columnDefs;
};

export default useContactColumnDefs;
